import type { Font, FontFamily } from '@/api/types/font-family'
import type { LoadedFontVariant } from '../types/loaded-font-variant'

export function buildFontIdLookup(families: FontFamily[]): Map<number, LoadedFontVariant> {
  const map = new Map<number, LoadedFontVariant>()

  for (const family of families) {
    for (const font of family.fonts ?? []) {
      map.set(font.fontId, {
        fontFamilyId: family.fontFamilyId,
        familyName: family.familyName,
        bold: font.bold,
        italic: font.italic,
        fontStyleName: font.fontStyle?.fontStyleName,
      })
    }
  }

  return map
}

/** Map `${fontFamilyId}:${bold}:${italic}` → fontId for reverse variant lookup. */
export function buildFontIdByVariantLookup(families: FontFamily[]): Map<string, number> {
  const map = new Map<string, number>()

  for (const family of families) {
    for (const font of family.fonts ?? []) {
      map.set(`${family.fontFamilyId}:${font.bold}:${font.italic}`, font.fontId)
    }
  }

  return map
}

/**
 * Lookup key for matching a CSS `font-family` name against a permissible family (WMPCM-9199).
 *
 * The reverse lookup used to be an exact, case-sensitive `Map.get` on `familyName`, so a pasted
 * run whose family WAS licensed still missed on any cosmetic difference — `"times new roman"` vs
 * `Times New Roman`, a stray double space, or the source app emitting the display name. A miss is
 * silent and indistinguishable from "font not available": the run keeps the caret's font, which is
 * exactly the formatting loss QA reported.
 */
export function normalizeFontFamilyKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

/**
 * Normalized CSS-name → family id, accepting both the CSS `familyName` and the human `displayName`
 * so either spelling resolves. `familyName` is registered last and therefore wins a collision: it
 * is the name the `FontFace` is actually registered under, so it is the one that will render.
 */
export function buildFontFamilyNameToId(families: FontFamily[]): Map<string, number> {
  const map = new Map<string, number>()

  for (const family of families) {
    if (family.displayName) map.set(normalizeFontFamilyKey(family.displayName), family.fontFamilyId)
  }

  for (const family of families) {
    if (family.familyName) map.set(normalizeFontFamilyKey(family.familyName), family.fontFamilyId)
  }

  return map
}

export function buildFontFamilyIdToFamilyName(families: FontFamily[]): Map<number, string> {
  const map = new Map<number, string>()

  for (const family of families) {
    map.set(family.fontFamilyId, family.familyName)
  }

  return map
}

export function findFontFamilyById(
  families: FontFamily[],
  fontFamilyId: number
): FontFamily | undefined {
  return families.find((f) => f.fontFamilyId === fontFamilyId)
}

export function findFontAndFamilyByFontId(
  families: FontFamily[],
  fontId: number
): { font: Font; family: FontFamily } | undefined {
  for (const family of families) {
    const font = family.fonts?.find((ff) => ff.fontId === fontId)

    if (font) return { font, family }
  }

  return undefined
}

/** Prefer Regular (not bold, not italic); else first variant. */
export function pickDefaultFontIdForFamily(family: FontFamily | undefined): number | undefined {
  if (!family?.fonts?.length) return undefined
  const regular = family.fonts.find((f) => !f.bold && !f.italic)

  return (regular ?? family.fonts[0]).fontId
}
